/**
 * Number display precision.
 *
 * Two rules act in sequence on every value: the value is first rounded to a
 * number of significant figures, then the result is capped to a maximum number
 * of decimal places. The cap can prevent the requested significant figures from
 * ever reaching the screen, which is the tension this module reports back to the
 * caller through PrecisionLoss.
 */

export interface PrecisionSettings {
	/** When false, every digit survives to the decimal cap. */
	useSignificantFigures: boolean
	significantFigures: number
	maximumDecimals: number
	groupThousands: boolean
}

/** How much of the source value survived formatting. */
export type PrecisionLoss = "none" | "capped" | "zero"

export interface FormattedNumber {
	display: string
	loss: PrecisionLoss
	/** Plain-language explanation, empty when nothing was lost. */
	note: string
}

export const SIGNIFICANT_FIGURE_LIMITS = { minimum: 1, maximum: 15 } as const
export const DECIMAL_LIMITS = { minimum: 0, maximum: 12 } as const

export const DEFAULT_PRECISION_SETTINGS: PrecisionSettings = {
	useSignificantFigures: true,
	significantFigures: 4,
	maximumDecimals: 6,
	groupThousands: true,
}

/** Above this magnitude toFixed switches to exponential notation on its own. */
const FIXED_NOTATION_CEILING = 1e21

const SUPERSCRIPT_DIGITS: Readonly<Record<string, string>> = {
	"-": "⁻",
	"0": "⁰",
	"1": "¹",
	"2": "²",
	"3": "³",
	"4": "⁴",
	"5": "⁵",
	"6": "⁶",
	"7": "⁷",
	"8": "⁸",
	"9": "⁹",
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(Math.max(value, minimum), maximum)
}

function toSuperscript(value: number): string {
	return value
		.toString()
		.split("")
		.map((character) => SUPERSCRIPT_DIGITS[character] ?? character)
		.join("")
}

/** Decimal places in the shortest exact representation of a value. */
function decimalPlacesOf(value: number): number {
	const text = Math.abs(value).toString()

	if (text.includes("e")) {
		const [mantissa, exponentText] = text.split("e")
		const exponent = parseInt(exponentText, 10)
		const mantissaDecimals = mantissa.includes(".") ? mantissa.split(".")[1].length : 0
		return Math.max(0, mantissaDecimals - exponent)
	}

	return text.includes(".") ? text.split(".")[1].length : 0
}

/**
 * Expands a value to plain decimal notation. Number.toString() switches to
 * exponential below 1e-6, which hides the very digits a precision screen exists
 * to show.
 */
export function toPlainDecimalString(value: number): string {
	const text = value.toString()
	if (!text.includes("e")) return text

	const [mantissa, exponentText] = Math.abs(value).toString().split("e")
	const exponent = parseInt(exponentText, 10)
	const [integerDigits, decimalDigits = ""] = mantissa.split(".")
	const sign = value < 0 ? "-" : ""

	if (exponent < 0) {
		const leadingZeros = "0".repeat(Math.max(0, -exponent - integerDigits.length))
		return `${sign}0.${leadingZeros}${integerDigits}${decimalDigits}`
	}

	const trailingZeros = "0".repeat(Math.max(0, exponent - decimalDigits.length))
	const digits = `${integerDigits}${decimalDigits}${trailingZeros}`
	const pointPosition = integerDigits.length + exponent
	return sign + (pointPosition >= digits.length ? digits : `${digits.slice(0, pointPosition)}.${digits.slice(pointPosition)}`)
}

function withThousandsSeparators(integerDigits: string): string {
	return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

/** Renders a value at a fixed decimal count, dropping trailing zeros. */
function renderFixed(value: number, decimals: number, groupThousands: boolean): string {
	if (Math.abs(value) >= FIXED_NOTATION_CEILING) return toPlainDecimalString(value)

	const isNegative = value < 0
	const [integerPart, decimalPart = ""] = Math.abs(value).toFixed(decimals).split(".")
	const trimmedDecimals = decimalPart.replace(/0+$/, "")
	const integerDigits = groupThousands ? withThousandsSeparators(integerPart) : integerPart

	return `${isNegative ? "-" : ""}${integerDigits}${trimmedDecimals ? `.${trimmedDecimals}` : ""}`
}

/** The power of ten a value sits on, rendered as 10⁻⁷ / 10⁰ / 10⁶. */
export function magnitudeLabel(value: number): string {
	if (value === 0 || !isFinite(value)) return "0"
	return `10${toSuperscript(Math.floor(Math.log10(Math.abs(value))))}`
}

export function roundWithPrecision(value: number, settings: PrecisionSettings): number {
	if (!isFinite(value) || value === 0) return value

	const significantFigures = clamp(settings.significantFigures, SIGNIFICANT_FIGURE_LIMITS.minimum, SIGNIFICANT_FIGURE_LIMITS.maximum)
	const maximumDecimals = clamp(settings.maximumDecimals, DECIMAL_LIMITS.minimum, DECIMAL_LIMITS.maximum)
	const roundedValue = settings.useSignificantFigures ? Number(value.toPrecision(significantFigures)) : value

	return Number(roundedValue.toFixed(Math.min(decimalPlacesOf(roundedValue), maximumDecimals)))
}

export function formatWithPrecision(value: number, settings: PrecisionSettings): FormattedNumber {
	if (!isFinite(value)) return { display: value.toString(), loss: "none", note: "" }
	if (value === 0) return { display: "0", loss: "none", note: "" }

	const maximumDecimals = clamp(settings.maximumDecimals, DECIMAL_LIMITS.minimum, DECIMAL_LIMITS.maximum)
	const roundedValue = settings.useSignificantFigures
		? Number(value.toPrecision(clamp(settings.significantFigures, SIGNIFICANT_FIGURE_LIMITS.minimum, SIGNIFICANT_FIGURE_LIMITS.maximum)))
		: value
	const decimalsWanted = decimalPlacesOf(roundedValue)
	const decimalsUsed = Math.min(decimalsWanted, maximumDecimals)
	const finalValue = roundWithPrecision(value, settings)
	const display = renderFixed(finalValue, decimalsUsed, settings.groupThousands)

	if (finalValue === 0) {
		return {
			display,
			loss: "zero",
			note: `Rounds to zero at ${maximumDecimals} ${maximumDecimals === 1 ? "decimal" : "decimals"}`,
		}
	}

	if (decimalsUsed < decimalsWanted) {
		const digitsDropped = decimalsWanted - decimalsUsed
		return {
			display,
			loss: "capped",
			note: `Decimal limit drops ${digitsDropped} ${digitsDropped === 1 ? "digit" : "digits"}`,
		}
	}

	return { display, loss: "none", note: "" }
}
